import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { useEffect, useState } from 'react';
import { ConfirmModalMEP } from './ConfirmModalMEP.jsx';

export const FormMEP = ({ data, onSubmit, profile, onClose }) => {
  const [dates, setDates] = useState({
    start: new Date(),
    end: null,
  });
  const date = new Date(data.scheduledAt);
  const [limit, setLimit] = useState({
    start: new Date(),
    end: date.setMinutes(date.getUTCMinutes() + 15),
  });
  const [formError, setFormError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [update, setUpdate] = useState(null);
  const [releaseNumber, setReleaseNumber] = useState(null);
  const [link, setLink] = useState(null);
  const [isFix, setIsFix] = useState(true);

  const handleChangeReason = (event) => {
    setIsFix(event.target.value);
  };
  const handleSubmit = () => {
    setOpenModal(false);
    onSubmit(update);
    onClose(null);
  };

  const handleConfirmModal = () => {
    if (dates.start === null) {
      setFormError('Veuillez renseigner une date de début valide');
      return;
    }
    if ((releaseNumber === null || link === null) && !isFix) {
      setFormError('Veuillez donner un numéro de release et un lien valide');
      return null;
    }
    setFormError(null);
    const start = new Date(dates?.start).toLocaleTimeString('fr', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const end = new Date(dates?.end).toLocaleTimeString('fr', {
      hour: '2-digit',
      minute: '2-digit',
    });
    let content = `Attention un déploiement est en cours de préparation, vous serez déconnectés à ${start}. Veillez à sauvegarder votre travail.`;
    if (dates?.end != null) {
      content = content + ` Fin du déploiement prévu à ${end} !`;
    }
    setUpdate({
      code: 'WAITING_DEPLOY',
      scheduledAt: dates.start,
      estimedEndAt: dates?.end,
      updatedAt: data?.updatedAt,
      author: profile,
      endAt: null,
      startAt: null,
      message: content,
      releaseNumber: releaseNumber,
      link: link,
      isFix: isFix,
    });
    setOpenModal(true);
  };

  useEffect(() => {
    if (dates.start != null) {
      const minEnd = new Date(dates.start?.getTime());
      minEnd.setUTCMinutes(dates.start?.getUTCMinutes() + 15);
      setLimit({ ...limit, end: minEnd });
    }
  }, [dates]);

  return (
    <Box
      sx={{
        minHeight: '40rem',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'start',
        gap: 1,
      }}
    >
      <Card
        sx={{
          width: '100%',
          padding: 2,
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <FormControl
          error={Boolean(formError != null)}
          fullWidth
          sx={{ mt: 2 }}
        >
          <Typography
            color="primary"
            sx={{ fontSize: '1em', fontWeight: 'bold' }}
          >
            Programmation d&apos;un déploiement
          </Typography>
          {formError != null ? (
            <FormHelperText error sx={{ fontSize: '1rem', mt: 2 }}>
              {formError}
            </FormHelperText>
          ) : null}
          <Typography color="primary" variant="subtitle1">
            Délais de déploiement
          </Typography>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: 2,
              mt: 2,
            }}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={fr}
            >
              <DateTimePicker
                label="Début"
                ampm={false}
                onChange={(newValue) => setDates({ ...dates, start: newValue })}
                minDateTime={limit.start}
                defaultValue={dates.start}
              />
              <DateTimePicker
                label="Fin estimée"
                ampm={false}
                onChange={(newValue) => setDates({ ...dates, end: newValue })}
                minDateTime={limit.end}
              />
            </LocalizationProvider>
          </Box>
          <Divider sx={{ m: 2 }} />
          <Typography color="primary" variant="subtitle1">
            Motif du déploiement
          </Typography>
          <Box
            sx={{
              display: 'flex',
              width: '30%',
              mt: 2,
              mb: 2,
            }}
          >
            <Select value={isFix} onChange={handleChangeReason} size="small">
              <MenuItem value={true}>HOT FIX - Maintenance de Pandora</MenuItem>
              <MenuItem value={false}>DEPLOY - Mise à jour de Pandora</MenuItem>
            </Select>
          </Box>

          <Typography color="primary" variant="subtitle1">
            Informations pour la patch note:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              width: '50%',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
            }}
          >
            <TextField
              label="Numéro de release"
              autoFocus
              required={isFix}
              margin="dense"
              value={releaseNumber}
              type="text"
              fullWidth
              variant="outlined"
              onChange={(e) => setReleaseNumber(e.target.value)}
              helperText="expl: X.XX.XX"
              disabled={isFix}
            />

            <TextField
              required={!isFix}
              label="Lien de documentation"
              rows={4}
              value={link}
              type="text"
              fullWidth
              onChange={(e) => setLink(e.target.value)}
              disabled={isFix}
            />
          </Box>
        </FormControl>

        {data?.author && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Typography
              color="primary"
              sx={{ fontSize: '0.8em', fontWeight: 'bold' }}
            >
              {data?.updatedAt
                ? 'Dernier déploiement le ' +
                  format(new Date(data?.updatedAt), "dd MMMM yyyy 'à' HH:mm ", {
                    locale: fr,
                  })
                : 'Aucune mise à jour '}
              par {data?.author?.firstName} {data?.author?.lastName}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button variant="outlined" color="info" onClick={() => onClose(null)}>
            Annuler
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              endIcon={<CheckIcon />}
              variant="contained"
              color="primary"
              onClick={handleConfirmModal}
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Card>
      <ConfirmModalMEP
        isOpen={openModal}
        handleClose={() => setOpenModal(false)}
        handleConfirm={() => handleSubmit()}
        formData={update}
      />
    </Box>
  );
};
